import { useCallback, useEffect, useMemo, useRef, useState, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Banner } from '@figma/fpl-components';
import { ColorTheme } from '@figma/fpl-themes';
import { color } from '@/colors';
import { themeColor } from '@/UI/themeColors';
import { Box } from '@/UI/Box/Box';
import { FlexCenVer } from '@/UI/styles';
import { TextSmRg, TextSmMd } from '@/UI/AppTypography/AppTypography';
import { AppIconButton } from '@/UI/Buttons/AppIconButton';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { CompanyIcon, LinkSmallIcon, PublishedCommunityIcon, ThreeDotsHorizontalIcon, UpdatesIcon24 } from '@/UI/Icons';
import { AppContextMenu } from '@/UI/AppContextMenu/AppContextMenu';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { I18N_KEYS } from '@/language/keys';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { useCommunityPublishEligibility } from '@/hooks/useCommunityPublishEligibility';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { useCopyToClipboard } from '@/hooks/useCopyToClipboard';
import { useLinkedFigmaAccount } from '@/hooks/useLinkedFigmaAccount';
import { type ToolCommunityMetadata, type WorkflowCommunityMetadata } from '@/api/workflowCommunityPublishing.api';
import { WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES } from '@/consts/analytics.consts';
import { MENU_NAMES } from '@/consts/menu-names.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { useCommunityLinkActions } from './useCommunityLinkActions';
import { useUnpublishWorkflowFromCommunity } from './useUnpublishWorkflowFromCommunity';
import { useUnpublishToolFromCommunity } from './useUnpublishToolFromCommunity';
import { isLiveAndVerified, isPendingReview } from './workflowCommunityStatus';
import PublishToCommunityModal from './PublishToCommunityModal';
import { useWorkflowCommunityPublishReadiness } from './useWorkflowCommunityPublishReadiness';
import {
  getCommunityPublishEligibilityBlockerMessage,
  getCommunityPublishReadinessBlockerMessage,
  hasRuntimeCommunityPublishBlocker,
} from './workflowCommunityPublishReadinessMessages';
import type { MenuAction } from '@/components/Menu/Actions';

const BANNER = I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.BANNER;
const UNPUBLISH = I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.UNPUBLISH;

interface CommunityBannerProps {
  background: string;
  icon: ReactElement;
  title: string;
  subtitle?: string;
  actions?: ReactNode;
  menuItems?: MenuAction[];
  testId?: string;
}

const CommunityBanner = ({ background, icon, title, subtitle, actions, menuItems, testId }: CommunityBannerProps) => {
  const { t } = useTranslation();
  const [menuAnchorEl, setMenuAnchorEl] = useState<HTMLElement | null>(null);
  const isFplMigrationEnabled = useFplMigrationFlag();

  const menu =
    menuItems && menuItems.length > 0 ? (
      <>
        <AppIconButton
          aria-label={t(I18N_KEYS.GENERAL.ARIA.OPEN_PUBLISHED_WORKFLOW_MENU)}
          onClick={(e) => setMenuAnchorEl(menuAnchorEl ? null : e.currentTarget)}
          width={24}
          height={24}
          sx={{
            color: color.Black100,
            bgcolor: menuAnchorEl ? color.Black16_T : 'transparent',
            '&:hover': {
              bgcolor: color.Black16_T,
              color: color.Black100,
            },
          }}
        >
          <ThreeDotsHorizontalIcon width={24} height={24} />
        </AppIconButton>
        <AppContextMenu
          open={menuAnchorEl !== null}
          anchorEl={menuAnchorEl}
          onClose={() => setMenuAnchorEl(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
          transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          offset={{ top: '6px' }}
          width="248px"
          items={menuItems}
          colorTheme={{ brand: 'weave', mode: 'light' }}
        />
      </>
    ) : null;

  if (isFplMigrationEnabled) {
    return (
      <Box sx={{ flexShrink: 0 }}>
        <ColorTheme mode="light" brand="weave">
          <Banner.FullWidth data-testid={testId} icon={icon}>
            <Banner.Message title={title}>{subtitle}</Banner.Message>
            <FlexCenVer sx={{ gap: 1, flexShrink: 0 }}>
              {actions}
              {menu}
            </FlexCenVer>
          </Banner.FullWidth>
        </ColorTheme>
      </Box>
    );
  }

  return (
    <FlexCenVer
      data-testid={testId}
      sx={{
        justifyContent: 'space-between',
        pl: 2,
        pr: 1.5,
        py: 1.5,
        gap: 1,
        background,
        flexShrink: 0,
      }}
    >
      <FlexCenVer sx={{ flex: 1, gap: 2, color: color.Black100, minWidth: 0, overflow: 'hidden' }}>
        {icon}
        <FlexCenVer sx={{ flex: 1, gap: 1, minWidth: 0, overflow: 'hidden' }}>
          <TextSmMd color={color.Black100} noWrap sx={{ flexShrink: 0 }}>
            {title}
          </TextSmMd>
          {subtitle ? (
            <TextSmRg color={color.Black64_T} noWrap sx={{ minWidth: 0 }}>
              {subtitle}
            </TextSmRg>
          ) : null}
        </FlexCenVer>
      </FlexCenVer>
      <FlexCenVer sx={{ gap: 1, flexShrink: 0 }}>
        {actions}
        {menu}
      </FlexCenVer>
    </FlexCenVer>
  );
};

export type CommunityPublishStatusBannerType = 'published' | 'pending' | 'updates_available';

/** A tool publishes under its `weave_app_id` (= `WorkflowTool.id`) but still lives inside a workflow, so it carries both ids. */
export type CommunityPublishBannerResource =
  | { kind: 'workflow'; workflowId: string; name: string }
  | { kind: 'tool'; toolId: string; workflowId: string; name: string };

interface CommunityBannerCopy {
  titleKey: string;
  subtitleKey: string;
}

const STATUS_CONFIG: Record<
  CommunityPublishStatusBannerType,
  {
    background: string;
    workflow: CommunityBannerCopy;
    /**
     * Absent on `updates_available`: a tool always runs its workflow's latest deploy, so it has
     * nothing to re-publish and only ever renders `published` / `pending`.
     */
    tool?: CommunityBannerCopy;
  }
> = {
  published: {
    background: themeColor.white100Secondary,
    workflow: { titleKey: BANNER.PUBLISHED.TITLE, subtitleKey: BANNER.PUBLISHED.SUBTITLE },
    tool: { titleKey: BANNER.PUBLISHED.TITLE_TOOL, subtitleKey: BANNER.PUBLISHED.SUBTITLE_TOOL },
  },
  pending: {
    background: themeColor.white100Secondary,
    workflow: { titleKey: BANNER.PENDING.TITLE, subtitleKey: BANNER.PENDING.SUBTITLE },
    tool: { titleKey: BANNER.PENDING.TITLE_TOOL, subtitleKey: BANNER.PENDING.SUBTITLE_TOOL },
  },
  updates_available: {
    background: themeColor.white100Secondary,
    workflow: { titleKey: BANNER.UPDATES_AVAILABLE.TITLE, subtitleKey: BANNER.UPDATES_AVAILABLE.SUBTITLE },
  },
};

const UNPUBLISH_CONFIG = {
  workflow: { titleKey: UNPUBLISH.CONFIRM_TITLE, bodyKey: UNPUBLISH.CONFIRM_BODY, bodyNameVar: 'workflowName' },
  tool: { titleKey: UNPUBLISH.CONFIRM_TITLE_TOOL, bodyKey: UNPUBLISH.CONFIRM_BODY_TOOL, bodyNameVar: 'toolName' },
} as const;

interface CommunityPublishStatusBannerProps {
  bannerType: CommunityPublishStatusBannerType;
  metadata: WorkflowCommunityMetadata | ToolCommunityMetadata;
  resource: CommunityPublishBannerResource;
}

/**
 * Owner-facing banner for a resource published to (or pending on) Figma Community. Shared by the
 * workflow and tool surfaces; they differ only in copy, publish/unpublish target, and how each detects
 * an available update (the caller resolves `bannerType`).
 */
export const CommunityPublishStatusBanner = ({ bannerType, metadata, resource }: CommunityPublishStatusBannerProps) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const openConfirmationDialog = useConfirmationDialogStore((s) => s.openConfirmationDialog);
  const isTool = resource.kind === 'tool';
  const source = isTool ? ('tool_banner' as const) : ('workflow_banner' as const);
  // Primitives, not `resource` itself: callers build it inline, so its identity changes every render.
  const { kind, workflowId, name: resourceName } = resource;
  const toolId = resource.kind === 'tool' ? resource.toolId : null;
  const trackingContext = useMemo(
    () => ({
      source,
      surface: isTool ? ('tool' as const) : ('workflow' as const),
      mode: 'update' as const,
      destination: 'community' as const,
      workflowId,
      resourceId: metadata.resource_id,
      ...(toolId ? { toolId } : {}),
    }),
    [isTool, metadata.resource_id, source, toolId, workflowId],
  );
  const { goToCommunity } = useCommunityLinkActions(metadata.community_url, trackingContext);
  const { copied, copyToClipboard } = useCopyToClipboard({
    trackingName: WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.LINK_ACTION_CLICKED,
    trackingPayload: {
      ...trackingContext,
      action: 'copy_link',
    },
  });
  // Both are plain callbacks (no fetching), so calling both keeps hook order stable at no cost.
  const unpublishWorkflow = useUnpublishWorkflowFromCommunity(workflowId, trackingContext);
  const unpublishTool = useUnpublishToolFromCommunity(toolId ?? workflowId, trackingContext);
  const unpublish = isTool ? unpublishTool : unpublishWorkflow;
  const [publishModalOpen, setPublishModalOpen] = useState(false);
  const trackedEntrypointViewKeysRef = useRef(new Set<string>());
  const readiness = useWorkflowCommunityPublishReadiness();
  const communityPublishEligibility = useCommunityPublishEligibility(isTool);
  const { data: figmaConnection, isLoading: isLoadingFigmaConnection } = useLinkedFigmaAccount();

  const handleUnpublishClick = useCallback(() => {
    track(
      WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.UNPUBLISH_DIALOG_OPENED,
      {
        ...trackingContext,
        publishingStatus: metadata.publishing_status,
      },
      TrackTypeEnum.Product,
    );
    const unpublishCopy = UNPUBLISH_CONFIG[kind];
    openConfirmationDialog({
      title: t(unpublishCopy.titleKey),
      message: t(unpublishCopy.bodyKey, { [unpublishCopy.bodyNameVar]: resourceName }),
      confirmBtnText: t(UNPUBLISH.CONFIRM_ACTION),
      destructive: true,
      width: 'sm',
      onConfirm: unpublish,
    });
  }, [kind, metadata.publishing_status, openConfirmationDialog, resourceName, t, track, trackingContext, unpublish]);

  const config = STATUS_CONFIG[bannerType];
  const copy = isTool && config.tool ? config.tool : config.workflow;
  const eligibilityReason = communityPublishEligibility.reason === 'unpaid' ? null : communityPublishEligibility.reason;
  const eligibilityMessage = getCommunityPublishEligibilityBlockerMessage(eligibilityReason, t);
  const figmaConnectionMessage =
    !isLoadingFigmaConnection && figmaConnection?.connected !== true
      ? t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.BANNER.UPDATES_AVAILABLE.RELINK_FIGMA_ACCOUNT)
      : null;
  const readinessMessage = hasRuntimeCommunityPublishBlocker(readiness)
    ? getCommunityPublishReadinessBlockerMessage(readiness, t)
    : null;
  const publishChangesTooltip = eligibilityMessage ?? figmaConnectionMessage ?? readinessMessage;
  const isPublishChangesDisabled = isLoadingFigmaConnection || Boolean(publishChangesTooltip);
  const expectedResourceId = toolId ?? workflowId;
  const metadataResourceId = 'weave_app_id' in metadata ? metadata.weave_app_id : metadata.weave_workflow_id;
  const isMetadataForDifferentResource = metadataResourceId !== expectedResourceId;
  const entrypointViewKey = `${expectedResourceId}:${bannerType}`;
  const publishChangesDisabledReason =
    bannerType !== 'updates_available'
      ? null
      : eligibilityMessage
        ? 'eligibility'
        : figmaConnectionMessage
          ? 'figma_connection'
          : readinessMessage
            ? 'readiness'
            : null;

  useEffect(() => {
    if (
      trackedEntrypointViewKeysRef.current.has(entrypointViewKey) ||
      isLoadingFigmaConnection ||
      isMetadataForDifferentResource ||
      communityPublishEligibility.isLoading
    ) {
      return;
    }

    trackedEntrypointViewKeysRef.current.add(entrypointViewKey);
    track(
      WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.ENTRYPOINT_VIEWED,
      {
        ...trackingContext,
        bannerType,
        disabledReason: publishChangesDisabledReason,
        eligibilityReason,
        readinessBlocker: readiness.blocker,
        figmaConnected: figmaConnection?.connected === true,
        isPublished: true,
      },
      TrackTypeEnum.Product,
    );
  }, [
    bannerType,
    communityPublishEligibility.isLoading,
    entrypointViewKey,
    eligibilityReason,
    figmaConnection?.connected,
    isLoadingFigmaConnection,
    isMetadataForDifferentResource,
    publishChangesDisabledReason,
    readiness.blocker,
    track,
    trackingContext,
  ]);

  const handlePublishChangesClick = useCallback(() => {
    if (isPublishChangesDisabled) return;
    track(
      WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.ENTRYPOINT_CLICKED,
      {
        ...trackingContext,
        nextStep: 'open_publish_modal',
        bannerType,
        eligibilityReason,
        readinessBlocker: readiness.blocker,
        figmaConnected: figmaConnection?.connected === true,
        isPublished: true,
      },
      TrackTypeEnum.Product,
    );
    setPublishModalOpen(true);
  }, [
    bannerType,
    eligibilityReason,
    figmaConnection?.connected,
    isPublishChangesDisabled,
    readiness.blocker,
    track,
    trackingContext,
  ]);

  const icon =
    bannerType === 'updates_available' ? (
      <UpdatesIcon24 width={12} height={12} />
    ) : (
      <PublishedCommunityIcon width={24} height={24} />
    );
  const actions =
    bannerType === 'updates_available' ? (
      <>
        <ButtonContained size="xs" mode="outlined-light" onClick={goToCommunity}>
          {t(BANNER.VIEW_IN_COMMUNITY)}
        </ButtonContained>
        <Tooltip title={publishChangesTooltip} placement="bottom">
          <span style={{ display: 'inline-flex', cursor: isPublishChangesDisabled ? 'not-allowed' : 'inherit' }}>
            <ButtonContained
              size="xs"
              mode="filled-secondary"
              data-testid="community-publish-banner-publish-changes"
              disabled={isPublishChangesDisabled}
              onClick={handlePublishChangesClick}
              sx={{
                '&.Mui-disabled': {
                  bgcolor: color.Black16_T,
                  color: color.Black64_T,
                  border: `1px solid ${color.Black16_T}`,
                },
              }}
            >
              {t(BANNER.UPDATES_AVAILABLE.PUBLISH_CHANGES)}
            </ButtonContained>
          </span>
        </Tooltip>
      </>
    ) : (
      <>
        <Tooltip
          title={t(I18N_KEYS.GENERAL.COPIED)}
          open={copied}
          disableHoverListener
          placement="bottom"
          slotProps={{
            popper: {
              modifiers: [{ name: 'offset', options: { offset: [0, -8] } }],
            },
          }}
        >
          <ButtonContained
            size="xs"
            mode="text"
            startIcon={<LinkSmallIcon width={16} height={16} />}
            onClick={() => {
              if (!metadata.community_url) return;
              void copyToClipboard(metadata.community_url);
            }}
            sx={{ color: color.Black100, '&:hover': { bgcolor: color.Black08_T } }}
          >
            {t(BANNER.COPY_LINK)}
          </ButtonContained>
        </Tooltip>
        <ButtonContained size="xs" mode="filled-secondary" onClick={goToCommunity}>
          {t(BANNER.VIEW_IN_COMMUNITY)}
        </ButtonContained>
      </>
    );

  // The menu auto-closes on action (both flag states), so callbacks don't dismiss it themselves.
  const menuActions = useMemo<MenuAction[]>(() => {
    const items: MenuAction[] = [];
    if (bannerType === 'updates_available') {
      items.push({
        biEventKey: { menu: MENU_NAMES.COMMUNITY_PUBLISH_STATUS_BANNER, action: 'copy_link' },
        name: t(BANNER.COPY_LINK),
        action: () => {
          if (!metadata.community_url) return;
          void copyToClipboard(metadata.community_url);
        },
      });
    }
    items.push({
      biEventKey: { menu: MENU_NAMES.COMMUNITY_PUBLISH_STATUS_BANNER, action: 'unpublish_menu_item' },
      name: t(BANNER.UNPUBLISH_MENU_ITEM),
      action: handleUnpublishClick,
    });
    return items;
  }, [bannerType, copyToClipboard, handleUnpublishClick, metadata.community_url, t]);

  return (
    <>
      <CommunityBanner
        testId={`community-publish-banner-${bannerType}`}
        background={config.background}
        icon={icon}
        title={t(copy.titleKey)}
        subtitle={t(copy.subtitleKey)}
        actions={actions}
        menuItems={menuActions}
      />
      {/* Mounted only while open: the workflow and tool banners can render together (the workflow one
          isn't scoped to a tab), and two mounted publish modals would duplicate this component's hooks. */}
      {publishModalOpen ? (
        <PublishToCommunityModal
          open
          onClose={() => setPublishModalOpen(false)}
          source={source}
          mode="update"
          tool={toolId ? { weaveAppId: toolId } : undefined}
        />
      ) : null}
    </>
  );
};

/** Maps metadata to a banner state, or `null` when the resource is neither live nor pending (no banner). */
export const deriveCommunityPublishBannerType = (
  metadata: WorkflowCommunityMetadata | ToolCommunityMetadata | null | undefined,
  hasUpdates = false,
): CommunityPublishStatusBannerType | null => {
  if (!metadata) return null;
  if (isLiveAndVerified(metadata)) return hasUpdates ? 'updates_available' : 'published';
  return isPendingReview(metadata) ? 'pending' : null;
};

interface OrgPublishedToolBannerProps {
  toolId: string;
  /** The parent workflow id, for analytics. */
  workflowId: string;
  toolName: string;
  orgName: string;
  orgPlanId: string;
}

/** Owner-facing banner for a tool published to a Figma org, with the same Unpublish menu as Community's. */
export const OrgPublishedToolBanner = ({
  toolId,
  workflowId,
  toolName,
  orgName,
  orgPlanId,
}: OrgPublishedToolBannerProps) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const openConfirmationDialog = useConfirmationDialogStore((s) => s.openConfirmationDialog);
  const trackingContext = useMemo(
    () => ({
      source: 'tool_banner' as const,
      surface: 'tool' as const,
      mode: 'update' as const,
      destination: 'org' as const,
      orgPlanId,
      workflowId,
      toolId,
    }),
    [orgPlanId, toolId, workflowId],
  );
  const unpublish = useUnpublishToolFromCommunity(toolId, trackingContext, orgName);
  const menuItems = useMemo<MenuAction[]>(
    () => [
      {
        biEventKey: { menu: MENU_NAMES.COMMUNITY_PUBLISH_STATUS_BANNER, action: 'unpublish_menu_item' },
        name: t(BANNER.UNPUBLISH_MENU_ITEM_ORG, { orgName }),
        action: () => {
          track(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.UNPUBLISH_DIALOG_OPENED, trackingContext, TrackTypeEnum.Product);
          openConfirmationDialog({
            title: t(UNPUBLISH.CONFIRM_TITLE_TOOL),
            message: t(UNPUBLISH.CONFIRM_BODY_TOOL_ORG, { toolName, orgName }),
            confirmBtnText: t(UNPUBLISH.CONFIRM_ACTION),
            destructive: true,
            width: 'sm',
            onConfirm: unpublish,
          });
        },
      },
    ],
    [openConfirmationDialog, orgName, t, toolName, track, trackingContext, unpublish],
  );

  return (
    <CommunityBanner
      testId="org-publish-banner-published"
      background={STATUS_CONFIG.published.background}
      icon={<CompanyIcon width={24} height={24} />}
      title={t(BANNER.PUBLISHED.TITLE_TOOL)}
      subtitle={t(BANNER.PUBLISHED.SUBTITLE_TOOL_ORG, { orgName })}
      menuItems={menuItems}
    />
  );
};
